import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_VERSION } from '../config/apiConfig';

export const ACCESS_TOKEN_STORAGE_KEY = 'AeroBott_access_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'AeroBott_refresh_token';
// Kept for backwards compatibility with any code that still imports the old name.
export const TOKEN_STORAGE_KEY = ACCESS_TOKEN_STORAGE_KEY;

type OnUnauthorized = () => void;
let onUnauthorized: OnUnauthorized | null = null;
export const setOnUnauthorized = (handler: OnUnauthorized | null) => {
  onUnauthorized = handler;
};

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
export const setTokens = (accessToken: string, refreshToken?: string) => {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
};
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
};

export const axiosClient = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  headers: { 'Content-Type': 'application/json' },
});

axiosClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInflight: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshInflight) return refreshInflight;
  const refresh = getRefreshToken();
  if (!refresh) return null;
  refreshInflight = axios
    .post<{ data: { access_token: string } }>(`${API_BASE_URL}${API_VERSION}/auth/refresh-token`, {
      refresh_token: refresh,
    })
    .then((response) => {
      const next = response.data?.data?.access_token || null;
      if (next) localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, next);
      return next;
    })
    .catch(() => null)
    .finally(() => {
      refreshInflight = null;
    });
  return refreshInflight;
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const isAuthEndpoint =
      typeof original?.url === 'string' &&
      ['/auth/login', '/auth/refresh-token', '/auth/register-client', '/auth/register-pilot'].some((suffix) =>
        original.url?.endsWith(suffix),
      );

    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const next = await refreshAccessToken();
      if (next) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${next}`;
        return axiosClient(original);
      }
      clearTokens();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);
